/**
 * useCertifiedFloorExtraSync — bills every floor of a living area calculation beyond the first.
 *
 * The base product covers one floor; each further floor is the `EXTRA_CERTIFIED_FLOOR` extra
 * ("Every additional floor"), billed per floor. Legacy selected it from the floor counter
 * (`FloorPlanCertificationStep.context.ts` `addFloors`/`removeFloors`); v2 keeps the line's extra
 * count at `numberOfFloors - 1` the same way, so the floors the client enters are the floors billed.
 *
 * The counter OWNS the extra: the drawer withholds it from its manual extras list
 * (`CONFIGURATOR_OWNED_EXTRA_KINDS`), so nothing else can set a count that disagrees.
 *
 * The extra is only there when Salesforce relates it to the product; until then this does nothing.
 */

import { useEffect } from 'react'

import { ProductKind } from 'constants/product'
import { logger } from 'utils/logging'
import type { CartItemId, ResolvedExtra } from 'models/purchaseFlowRevamp'

import type { CartMutationHandler } from '../../adapters/bridge'
import { useCartLineCatalogueEntry } from '../useCartLineCatalogueEntry'

/** The floors billed on top of the base product, which includes the first one. */
export const extraFloorCount = (numberOfFloors: number): number => Math.max(0, numberOfFloors - 1)

export function useCertifiedFloorExtraSync(
  cartItemId: CartItemId,
  numberOfFloors: number,
  onCartMutation?: CartMutationHandler
): ResolvedExtra | null {
  const { variant } = useCartLineCatalogueEntry(cartItemId)
  const extra = variant?.extras.find((e) => e.productKind === ProductKind.EXTRA_CERTIFIED_FLOOR) ?? null
  const extraFloors = extraFloorCount(numberOfFloors)
  const isCatalogueResolved = !!variant

  useEffect(() => {
    if (!extra || !onCartMutation) {
      // Unbilled floors: the catalogue offers no extra-floor product for this line, or nothing can attach it.
      if (isCatalogueResolved && extraFloors > 0) {
        logger.warn('PF revamp living area: additional floors not billed', { cartItemId, numberOfFloors, hasExtra: !!extra, hasHandler: !!onCartMutation })
      }
      return
    }
    onCartMutation(extraFloors > 0
      ? { type: 'select-extra', productId: extra.id, parentCartItemId: cartItemId, count: extraFloors }
      : { type: 'unselect-extra', productId: extra.id, parentCartItemId: cartItemId })
  }, [extra, extraFloors, cartItemId, onCartMutation, isCatalogueResolved, numberOfFloors])

  return extra
}
